import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ticketService, faqService } from '../services/api';

const StatCard = ({ title, value, icon, color, bgColor, link }) => (
  <Link
    to={link}
    className="group relative bg-white rounded-2xl p-6 shadow-sm border border-cream-200 hover:shadow-lg hover:border-primary-200 transition-all duration-300"
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
      </div>
      <div className={`flex-shrink-0 p-3 rounded-xl ${bgColor} group-hover:scale-110 transition-transform duration-300`}>
        {icon}
      </div>
    </div>
    <div className={`absolute bottom-0 left-0 right-0 h-1 ${color} rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity`}></div>
  </Link>
);

const QuickAction = ({ to, icon, title, description, bgColor }) => (
  <Link
    to={to}
    className="group flex items-center p-4 bg-white rounded-xl border border-cream-200 hover:border-primary-300 hover:shadow-md transition-all duration-200"
  >
    <div className={`flex-shrink-0 p-3 ${bgColor} rounded-xl group-hover:scale-110 transition-transform duration-200`}>
      {icon}
    </div>
    <div className="ml-4">
      <p className="text-sm font-semibold text-gray-900">{title}</p>
      <p className="text-xs text-gray-500 mt-0.5">{description}</p>
    </div>
    <svg className="ml-auto h-5 w-5 text-cream-400 group-hover:text-primary-500 group-hover:translate-x-1 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  </Link>
);

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [popularFaqs, setPopularFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, ticketsRes, faqsRes] = await Promise.all([
          ticketService.getStats(),
          ticketService.getMyTickets({ limit: 5 }),
          faqService.getPopular({ limit: 5 })
        ]);

        setStats(statsRes.data.data);
        setRecentTickets(ticketsRes.data.data);
        setPopularFaqs(faqsRes.data.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500"></div>
          <p className="mt-4 text-sm text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Welcome Header - Green theme */}
      <div className="relative overflow-hidden bg-gradient-to-r from-primary-900 via-primary-800 to-primary-900 rounded-2xl p-8 text-white">
        <div className="relative z-10">
          <p className="text-primary-300 text-sm font-medium">{getGreeting()}</p>
          <h1 className="text-2xl sm:text-3xl font-bold mt-1">
            Welcome back, {user?.firstName}!
          </h1>
          <p className="text-primary-100/80 mt-2 max-w-xl">
            Here&apos;s an overview of your support activity. You have{' '}
            <span className="text-primary-400 font-semibold">{stats?.open || 0} open tickets</span> that need attention.
          </p>
        </div>
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-40 h-40 bg-primary-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-20 -mb-8 w-32 h-32 bg-primary-400/15 rounded-full blur-2xl"></div>
        <div className="absolute top-1/2 right-8 transform -translate-y-1/2">
          <svg className="w-32 h-32 text-primary-400/10" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        {/* Green accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-400 via-primary-500 to-primary-400"></div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard
          title="Total Tickets"
          value={stats?.total || 0}
          color="bg-primary-600"
          bgColor="bg-primary-50"
          link="/tickets"
          icon={
            <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          }
        />
        <StatCard
          title="Open Tickets"
          value={stats?.open || 0}
          color="bg-primary-400"
          bgColor="bg-primary-100"
          link="/tickets?status=open"
          icon={
            <svg className="h-6 w-6 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatCard
          title="Closed Today"
          value={stats?.closed_today || 0}
          color="bg-emerald-500"
          bgColor="bg-emerald-50"
          link="/tickets?status=closed"
          icon={
            <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatCard
          title="High Priority"
          value={stats?.high_priority || 0}
          color="bg-red-500"
          bgColor="bg-red-50"
          link="/tickets?priority=high"
          icon={
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          }
        />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Tickets - Takes 2 columns */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-cream-200">
          <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Recent Tickets</h2>
              <p className="text-sm text-gray-500">Your latest support requests</p>
            </div>
            <Link
              to="/tickets"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center"
            >
              View all
              <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          <div className="divide-y divide-cream-100">
            {recentTickets.length > 0 ? (
              recentTickets.map((ticket) => (
                <Link
                  key={ticket.id}
                  to={`/tickets/${ticket.id}`}
                  className="flex items-center justify-between px-6 py-4 hover:bg-cream-50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {ticket.subject}
                    </p>
                    <p className="text-sm text-gray-500 flex items-center mt-1">
                      <span className="font-mono text-xs bg-cream-100 text-gray-600 px-2 py-0.5 rounded">
                        {ticket.ticket_number}
                      </span>
                      {ticket.team_name && (
                        <>
                          <span className="mx-2 text-cream-400">•</span>
                          <span>{ticket.team_name}</span>
                        </>
                      )}
                    </p>
                  </div>
                  <span
                    className="ml-4 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: `${ticket.status_color}15`, color: ticket.status_color }}
                  >
                    {ticket.status_name}
                  </span>
                </Link>
              ))
            ) : (
              <div className="px-6 py-12 text-center">
                <svg className="mx-auto h-12 w-12 text-cream-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-4 text-gray-500">No tickets yet.</p>
                <Link
                  to="/tickets/new"
                  className="mt-2 inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  Create your first ticket
                  <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Popular FAQs */}
        <div className="bg-white rounded-2xl shadow-sm border border-cream-200">
          <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Popular FAQs</h2>
              <p className="text-sm text-gray-500">Most viewed articles</p>
            </div>
            <Link
              to="/faq"
              className="text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center"
            >
              Browse
              <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
          <div className="divide-y divide-cream-100">
            {popularFaqs.length > 0 ? (
              popularFaqs.map((faq) => (
                <Link
                  key={faq.id}
                  to={`/faq/${faq.id}`}
                  className="block px-6 py-4 hover:bg-cream-50 transition-colors"
                >
                  <p className="text-sm font-medium text-gray-900 line-clamp-2">
                    {faq.question}
                  </p>
                  <div className="flex items-center mt-2 text-xs text-gray-500">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-primary-50 text-primary-700">
                      {faq.category_name}
                    </span>
                    <span className="mx-2">•</span>
                    <span>{faq.view_count} views</span>
                  </div>
                </Link>
              ))
            ) : (
              <div className="px-6 py-12 text-center">
                <svg className="mx-auto h-12 w-12 text-cream-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="mt-4 text-gray-500">No FAQs available.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl shadow-sm border border-cream-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickAction
            to="/tickets/new"
            bgColor="bg-primary-50"
            icon={
              <svg className="h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            }
            title="Create Ticket"
            description="Submit a new request"
          />
          <QuickAction
            to="/faq"
            bgColor="bg-primary-100"
            icon={
              <svg className="h-6 w-6 text-primary-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            }
            title="Search FAQ"
            description="Find answers fast"
          />
          <QuickAction
            to="/tickets"
            bgColor="bg-emerald-50"
            icon={
              <svg className="h-6 w-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            }
            title="My Tickets"
            description="View ticket history"
          />
          <QuickAction
            to="/profile"
            bgColor="bg-gray-50"
            icon={
              <svg className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            }
            title="My Profile"
            description="Update settings"
          />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
